<template>
  <div>
    <LeftSidebar
      :visible="visible"
      :title="$lang.registerCodeList"
      bodyClass="padding-y-16 overflow-y-hidden"
      titleClass="padding-x-16"
      @close="handleClose"
    >
      <template>
        <ListWrapper
          v-if="visible"
          ref="listWrapperReference"
          :url="url"
          :hasTitle="false"
          :hasAddButton="false"
          :isUseInSidebar="true"
          menuBarClass="padding-x-16"
          searchTxtClass="width-percent-100"
          :otherExportExcelParams="otherExportExcelParams"
          @getPermissions="getPermissions"
          @getLoading="getLoading"
        >
          <template slot-scope="{ items }">
            <LoadingWrapper :loading="listLoading">
              <template #loading>
                <Loading :count="8" />
              </template>
              <template #default>
                <b-row class="register-code">
                  <b-col
                    v-for="(item, index) in generateItems(items)"
                    :key="index"
                    cols="12"
                    class="register-code__item"
                  >
                    <div>
                      <div v-if="item.nationalCode" class="register-code__line">
                        <span class="register-code__title">
                          {{ $lang.nationalCode }}:
                        </span>
                        <CopyTextWrapper
                          :text="item.nationalCode"
                          textClass="color-complementary-400 font-size-14 cursor--pointer"
                        />
                      </div>
                      <div class="register-code__line">
                        <span class="register-code__title">
                          {{ $lang.exclusiveRegisterCode }}:
                        </span>
                        <CopyTextWrapper
                          :text="item.exclusiveRegisterCode"
                          textClass="color-complementary-400 font-size-14 cursor--pointer"
                        />
                      </div>
                      <div
                        v-if="item.parvandehNumber"
                        class="register-code__line"
                      >
                        <span class="register-code__title">
                          {{ $lang.useInParvandehNumber }}:
                        </span>
                        <CopyTextWrapper
                          :text="item.parvandehNumber"
                          textClass="color-complementary-400 font-size-14 cursor--pointer"
                        />
                      </div>
                    </div>
                    <span
                      class="register-code__icon"
                      @click="handleOpenRemoveModal(item.id)"
                    >
                      <mdicon name="trash-can-outline" size="24" />
                    </span>
                  </b-col>
                </b-row>
              </template>
            </LoadingWrapper>
          </template>
        </ListWrapper>
      </template>
    </LeftSidebar>
    <Teleport to="body">
      <Modal
        :show="isRemoveModal"
        :title="$lang.removeRegisterCode"
        :closeOnBackdrop="false"
        :hasClose="false"
        :okButton="true"
        :okButtonText="$lang.remove"
        okButtonVariant="filled-error"
        size="sm"
        :okLoading="removeLoading"
        :ok="handleRemove"
        @close="handleCloseRemoveModal"
      >
        <template #body>
          <div class="margin-bottom-24 font-size-14 color-complementary-300">
            {{ $lang.remove_sure_message.replace("{0}", $lang.registerCode) }}
          </div>
        </template>
      </Modal>
    </Teleport>
  </div>
</template>

<script>
import Teleport from "vue2-teleport";
import {
  LeftSidebar,
  ListWrapper,
  Modal,
  LoadingWrapper,
  CopyTextWrapper,
} from "@/components/re-design";
import Loading from "./Loading.vue";
import { pathOr } from "ramda";
import { generateApiUrl, generateError, toastAlert } from "@/shared/utils";
import { computed, inject, ref, toRefs } from "vue";
import { removeRegisterCode } from "@/modules/profile/services";

export default {
  components: {
    Teleport,
    LeftSidebar,
    ListWrapper,
    Modal,
    LoadingWrapper,
    CopyTextWrapper,
    Loading,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    // generate props data
    const { visible } = toRefs(props);

    const handleClose = () => emit("close");

    // handle reload list
    const listWrapperReference = ref(null);
    const handleReloadList = () => listWrapperReference.value.reload();

    const termCode = inject("TERM_CODE");
    const url = computed(() =>
      generateApiUrl(["profile", "term_register_code"], [termCode])
    );
    const permissions = ref([]);
    const getPermissions = (value) => (permissions.value = [...value]);

    const listLoading = ref(false);
    const getLoading = (value) => (listLoading.value = value);

    const generateItems = (items) => {
      return items.map((item) => {
        const id = pathOr(null, ["id"], item);
        const nationalCode = pathOr("", ["national_code"], item);
        const exclusiveRegisterCode = pathOr("", ["code"], item);
        const parvandehNumber = pathOr("", ["parvandeh_number"], item);

        return {
          id,
          nationalCode,
          exclusiveRegisterCode,
          parvandehNumber,
        };
      });
    };

    const otherExportExcelParams = { term_code: termCode };

    // remove an item
    const isRemoveModal = ref(false);
    const removeLoading = ref(false);
    const removeItemId = ref(null);
    const handleOpenRemoveModal = (id) => {
      removeItemId.value = id;
      isRemoveModal.value = true;
    };
    const handleCloseRemoveModal = () => (isRemoveModal.value = false);
    const handleRemove = async () => {
      try {
        removeLoading.value = true;
        const id = removeItemId.value;
        const response = await removeRegisterCode(termCode, id);
        const { message } = response;
        toastAlert(message);
        handleCloseRemoveModal();
        handleReloadList();
      } catch (error) {
        generateError(error);
      } finally {
        removeLoading.value = false;
      }
    };

    // return data to be used in template
    return {
      visible,
      url,
      listWrapperReference,
      isRemoveModal,
      removeLoading,
      handleClose,
      generateItems,
      handleOpenRemoveModal,
      handleCloseRemoveModal,
      handleRemove,
      getPermissions,
      listLoading,
      getLoading,
      otherExportExcelParams,
    };
  },
};
</script>
